import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion } from 'framer-motion';
import { memo, useState, type ReactNode } from 'react';
import * as Tooltip from './Tooltip';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { genericMemo } from '~/utils/react';

interface SliderTabOption<T> {
  value: T;
  children: ReactNode;
  label: string;
}

interface SliderTabDropdownItem<T> {
  label: ReactNode;
  value: T;
  type?: 'default' | 'danger' | 'divider' | 'content';
  onClick?: () => void;
  submenu?: SliderTabDropdownItem<T>[];
}

interface SliderTabDropdownGroup<T> {
  header: string;
  items: SliderTabDropdownItem<T>[];
}

interface SliderTabsProps<T> {
  selected: T;
  options: SliderTabOption<T>[];
  setSelected?: (selected: T) => void;
  withBorder?: boolean;
  buttonClassName?: string;
  separatorIndexes?: number[];
  dropdownItems?: SliderTabDropdownItem<T>[];
  dropdownGroups?: SliderTabDropdownGroup<T>[];
}

export type { SliderTabDropdownItem, SliderTabDropdownGroup };

export const SliderTabs = genericMemo(
  <T,>({
    selected,
    options,
    setSelected,
    withBorder = false,
    buttonClassName,
    separatorIndexes = [],
    dropdownItems = [],
    dropdownGroups = [],
  }: SliderTabsProps<T>) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const hasDropdown = dropdownItems.length > 0 || dropdownGroups.length > 0;
    const visibleOptions = options;

    return (
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            'flex items-center flex-wrap shrink-0 bg-slate-50 dark:bg-slate-900 overflow-hidden rounded-xl p-1',
            {
              'border border-bolt-elements-borderColor': withBorder,
            },
          )}
        >
          {visibleOptions.map((option, index) => {
            const isSelected = selected === option.value;
            const hasSeparatorAfter = separatorIndexes.includes(index);

            return (
              <div key={`${option.value}`} className="flex items-center">
                <SliderTabButton
                  setSelected={() => setSelected?.(option.value)}
                  label={option.label}
                  buttonClassName={buttonClassName}
                  selected={isSelected}
                >
                  {option.children}
                </SliderTabButton>
                {hasSeparatorAfter && index < visibleOptions.length - 1 && (
                  <div className="w-px h-5 bg-bolt-elements-borderColor mx-1 opacity-60" />
                )}
              </div>
            );
          })}
        </div>

        {hasDropdown && (
          <div className="flex items-center">
            {separatorIndexes.includes(visibleOptions.length - 1) && (
              <div className="w-px h-5 bg-bolt-elements-borderColor mx-1 opacity-60" />
            )}
            <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenu.Trigger className="bg-transparent p-0" aria-label="More Options">
                <Tooltip.Provider disableHoverableContent>
                  <Tooltip.Root delayDuration={300}>
                    <Tooltip.Trigger asChild>
                      <div
                        className={
                          'flex items-center bg-transparent text-sm px-2 py-1 rounded-full relative text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive pl-1 pr-1.5 h-5 opacity-90 hover:opacity-100'
                        }
                      >
                        <div className="i-ph:gear-six-duotone w-4 h-4" />
                      </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content side="bottom" align="center" sideOffset={6}>
                        More Options
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-max min-w-[220px] rounded-md bg-bolt-elements-background-depth-2 p-1 dark:shadow-lg border border-bolt-elements-borderColor"
                  asChild
                  align="start"
                  sideOffset={4}
                >
                  <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                    {dropdownItems.map((item) => {
                      if (item.type === 'divider') {
                        return <div key={String(item.value)} className="h-px bg-bolt-elements-borderColor my-1" />;
                      }

                      if (item.submenu) {
                        return (
                          <DropdownMenu.Sub key={String(item.value)}>
                            <DropdownMenu.SubTrigger
                              className={classNames(
                                'flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none data-[state=open]:bg-bolt-elements-item-backgroundActive',
                                item.type === 'danger'
                                  ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                  : 'text-bolt-elements-textPrimary',
                              )}
                            >
                              {item.label}
                              <div className="i-ph:caret-right w-4 h-4 ml-auto" />
                            </DropdownMenu.SubTrigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.SubContent
                                className="z-max min-w-[220px] rounded-md bg-bolt-elements-background-depth-2 p-1 dark:shadow-lg border border-bolt-elements-borderColor"
                                sideOffset={2}
                              >
                                {item.submenu.map((subItem) => {
                                  if (subItem.type === 'divider') {
                                    return (
                                      <div
                                        key={String(subItem.value)}
                                        className="h-px bg-bolt-elements-borderColor my-1"
                                      />
                                    );
                                  }

                                  if (subItem.type === 'content') {
                                    return <div key={String(subItem.value)}>{subItem.label}</div>;
                                  }

                                  return (
                                    <DropdownMenu.Item
                                      key={String(subItem.value)}
                                      className={classNames(
                                        'flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none',
                                        subItem.type === 'danger'
                                          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                          : 'text-bolt-elements-textPrimary',
                                      )}
                                      onClick={() => {
                                        if (subItem.onClick) {
                                          subItem.onClick();
                                        } else if (setSelected) {
                                          setSelected(subItem.value);
                                        }

                                        setDropdownOpen(false);
                                      }}
                                    >
                                      {subItem.label}
                                    </DropdownMenu.Item>
                                  );
                                })}
                              </DropdownMenu.SubContent>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Sub>
                        );
                      }

                      return (
                        <DropdownMenu.Item
                          key={String(item.value)}
                          className={classNames(
                            'flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none',
                            item.type === 'danger'
                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                              : 'text-bolt-elements-textPrimary',
                          )}
                          onClick={() => {
                            if (item.onClick) {
                              item.onClick();
                            } else if (setSelected) {
                              setSelected(item.value);
                            }

                            setDropdownOpen(false);
                          }}
                        >
                          {item.label}
                        </DropdownMenu.Item>
                      );
                    })}
                    {dropdownGroups.map((group, groupIndex) => (
                      <div key={group.header}>
                        {(dropdownItems.length > 0 || groupIndex > 0) && (
                          <div className="h-px bg-bolt-elements-borderColor my-1" />
                        )}
                        <div className="px-2 py-1 text-xs text-bolt-elements-text-secondary opacity-60">
                          {group.header}
                        </div>
                        {group.items.map((item) => {
                          if (item.type === 'divider') {
                            return <div key={String(item.value)} className="h-px bg-bolt-elements-borderColor my-1" />;
                          }

                          if (item.submenu) {
                            return (
                              <DropdownMenu.Sub key={String(item.value)}>
                                <DropdownMenu.SubTrigger
                                  className={classNames(
                                    'flex items-center gap-2 px-3 py-2 text-base cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none data-[state=open]:bg-bolt-elements-item-backgroundActive',
                                    item.type === 'danger'
                                      ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                      : 'text-bolt-elements-textPrimary',
                                  )}
                                >
                                  {item.label}
                                  <div className="i-ph:caret-right w-4 h-4 ml-auto" />
                                </DropdownMenu.SubTrigger>
                                <DropdownMenu.Portal>
                                  <DropdownMenu.SubContent
                                    className="z-max min-w-[220px] rounded-md bg-bolt-elements-background-depth-2 p-1 dark:shadow-lg border border-bolt-elements-borderColor"
                                    sideOffset={2}
                                  >
                                    {item.submenu.map((subItem) => {
                                      if (subItem.type === 'divider') {
                                        return (
                                          <div
                                            key={String(subItem.value)}
                                            className="h-px bg-bolt-elements-borderColor my-1"
                                          />
                                        );
                                      }

                                      if (subItem.type === 'content') {
                                        return <div key={String(subItem.value)}>{subItem.label}</div>;
                                      }

                                      return (
                                        <DropdownMenu.Item
                                          key={String(subItem.value)}
                                          className={classNames(
                                            'flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none',
                                            subItem.type === 'danger'
                                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                              : 'text-bolt-elements-textPrimary',
                                          )}
                                          onClick={() => {
                                            if (subItem.onClick) {
                                              subItem.onClick();
                                            } else if (setSelected) {
                                              setSelected(subItem.value);
                                            }

                                            setDropdownOpen(false);
                                          }}
                                        >
                                          {subItem.label}
                                        </DropdownMenu.Item>
                                      );
                                    })}
                                  </DropdownMenu.SubContent>
                                </DropdownMenu.Portal>
                              </DropdownMenu.Sub>
                            );
                          }

                          return (
                            <DropdownMenu.Item
                              key={String(item.value)}
                              className={classNames(
                                'flex items-center gap-2 px-3 py-2 text-base cursor-pointer rounded-sm hover:bg-bolt-elements-item-backgroundActive focus:bg-bolt-elements-item-backgroundActive outline-none',
                                item.type === 'danger'
                                  ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                                  : 'text-bolt-elements-textPrimary',
                              )}
                              onClick={() => {
                                if (item.onClick) {
                                  item.onClick();
                                } else if (setSelected) {
                                  setSelected(item.value);
                                }

                                setDropdownOpen(false);
                              }}
                            >
                              {item.label}
                            </DropdownMenu.Item>
                          );
                        })}
                      </div>
                    ))}
                  </motion.div>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        )}
      </div>
    );
  },
);

interface SliderTabButtonProps {
  selected: boolean;
  children: ReactNode;
  setSelected: () => void;
  buttonClassName?: string;
  label: string;
}

const SliderTabButton = memo(({ selected, children, setSelected, buttonClassName, label }: SliderTabButtonProps) => {
  return (
    <Tooltip.Provider disableHoverableContent>
      <Tooltip.Root delayDuration={300}>
        <Tooltip.Trigger asChild>
          <button
            onClick={setSelected}
            aria-label={label}
            aria-pressed={selected}
            className={classNames(
              'bg-transparent text-sm px-2 py-1 rounded-full relative',
              selected
                ? 'text-bolt-elements-item-contentAccent'
                : 'text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive',
              buttonClassName,
            )}
          >
            {children}
            {selected && (
              <motion.span
                layoutId="pill-tab-slider"
                transition={{ duration: 0.2, ease: cubicEasingFn }}
                className="absolute inset-0 z-0 bg-bolt-elements-item-backgroundAccent rounded-lg"
              />
            )}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="bottom" align="center" sideOffset={6}>
            {label}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
});
